import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import JewelCard from '../components/JewelCard';
import LivePriceBar from '../components/LivePriceBar';
import { useCart } from '../context/CartContext';
import './Section.css';

/** URL slug → API material */
const SLUG_TO_MATERIAL = {
  gold: 'gold',
  silver: 'silver',
  fancy: 'fancy',
  'gold-plated': 'gold_plated',
};

const SECTION_TITLES = {
  gold: 'Gold Collection',
  silver: 'Silver Collection',
  fancy: 'Fancy Items',
  gold_plated: 'Gold Plated',
};

const SECTION_SUBTITLES = {
  gold: 'Timeless pieces crafted in pure gold',
  silver: 'Elegant silver for every occasion',
  fancy: 'Unique designs and statement jewellery',
  gold_plated: 'Beautiful gold-plated jewellery at friendly prices',
};

const PAGE_SIZE = 24;

function buildCategoryPills(catsData, apiMaterial, usedCategoryIds) {
  const relevant = [];
  const seen = new Set();
  const add = (row) => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    relevant.push(row);
  };
  const appliesToSection = (c) => !c.material || c.material === apiMaterial;

  for (const cat of catsData) {
    if (!appliesToSection(cat)) continue;
    if (usedCategoryIds.has(cat.id)) {
      add(cat);
    }
    for (const sub of cat.subcategories || []) {
      if (!appliesToSection(sub)) continue;
      if (usedCategoryIds.has(sub.id)) {
        add({ ...sub, name: `${cat.name} › ${sub.name}` });
      }
    }
  }
  return relevant;
}

export default function Section() {
  const { material: materialSlug } = useParams();
  return <SectionView key={materialSlug} materialSlug={materialSlug} />;
}

function SectionView({ materialSlug }) {
  const apiMaterial = SLUG_TO_MATERIAL[materialSlug];

  const [jewels, setJewels] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    if (!apiMaterial) return;
    let cancelled = false;
    setLoading(true);
    setJewels([]);
    setTotal(0);

    const run = async () => {
      try {
        const qs = new URLSearchParams({
          material: apiMaterial,
          limit: String(PAGE_SIZE),
          offset: '0',
          facet: '1',
        });
        if (activeCategory != null) qs.set('category_id', String(activeCategory));

        const res = await fetch(`/api/jewels?${qs.toString()}`);
        const data = await res.json();
        if (cancelled) return;

        if (!res.ok || !Array.isArray(data.items)) {
          throw new Error(data.error || 'Failed to load');
        }

        setJewels(data.items);
        setTotal(typeof data.total === 'number' ? data.total : data.items.length);

        if (Array.isArray(data.facet_category_ids)) {
          const usedCategoryIds = new Set(
            data.facet_category_ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
          );
          const catsRes = await fetch('/api/categories');
          const catsData = await catsRes.json();
          if (!cancelled) {
            setCategories(buildCategoryPills(catsData, apiMaterial, usedCategoryIds));
          }
        } else if (!cancelled) {
          setCategories([]);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setJewels([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [apiMaterial, activeCategory]);

  const onLoadMore = async () => {
    if (loadingMore || jewels.length >= total) return;
    setLoadingMore(true);
    try {
      const qs = new URLSearchParams({
        material: apiMaterial,
        limit: String(PAGE_SIZE),
        offset: String(jewels.length),
      });
      if (activeCategory != null) qs.set('category_id', String(activeCategory));

      const res = await fetch(`/api/jewels?${qs.toString()}`);
      const data = await res.json();
      if (!res.ok || !Array.isArray(data.items)) {
        throw new Error(data.error || 'Failed to load');
      }
      setJewels((prev) => [...prev, ...data.items]);
      if (typeof data.total === 'number') setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  if (!apiMaterial) {
    return <Navigate to="/" replace />;
  }

  const heroSlug = materialSlug === 'gold-plated' ? 'gold-plated' : apiMaterial;
  const hasMore = jewels.length < total;

  return (
    <div className="section-page">
      <LivePriceBar />

      <div className="section-hero" data-material={heroSlug}>
        <h1>{SECTION_TITLES[apiMaterial] || 'Collection'}</h1>
        <p>{SECTION_SUBTITLES[apiMaterial] || 'Browse our selection'}</p>
      </div>

      {categories.length > 0 && (
        <div className="category-filters">
          <button
            type="button"
            className={`pill ${!activeCategory ? 'active' : ''}`}
            onClick={() => setActiveCategory(null)}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              type="button"
              key={cat.id}
              className={`pill ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="page-center"><p>Loading...</p></div>
      ) : jewels.length === 0 ? (
        <div className="page-center"><p>No items in this section yet.</p></div>
      ) : (
        <>
          <div className="section-grid">
            {jewels.map((j) => (
              <JewelCard key={j.id} jewel={j} onAddToCart={addItem} />
            ))}
          </div>
          {hasMore && (
            <div className="section-load-more-wrap">
              <button
                type="button"
                className="section-load-more"
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

